import React from 'react';
import { motion } from 'framer-motion';
import { Compass, TrendingUp, Eye } from 'lucide-react';

const About = () => {
  return (
    <section id="about" className="bg-black py-24">
      <div className="container mx-auto px-4 max-w-6xl">

        {/* Header */}
        <div className="mb-20">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-primary-400 text-sm font-semibold uppercase tracking-widest mb-3"
          >
            Who We Are
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white max-w-2xl leading-tight"
          >
            About Njord
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-16 h-0.5 bg-primary mt-6 origin-left"
          />
        </div>

        {/* Our Story — two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Our Story</h3>
            <p className="text-gray-400 leading-relaxed mb-5">
              Njord emerged from a keen observation: many businesses struggle with repetitive tasks while advanced AI technology remains inaccessible to most. Our mission is to bridge this gap as a leading AI consultancy.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Today, we proudly assist businesses across the UK in streamlining operations, reducing costs, and focusing on what truly matters — growing their business and serving their customers better.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            {/* Decorative card */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-black text-white/5 mb-2 select-none">Njord</div>
              <p className="text-gray-300 leading-relaxed italic text-lg">
                "Named after the Norse god of the sea and wind, Njord embodies our commitment to navigating the intricate waters of AI — harnessing the winds of technological change to propel our clients forward."
              </p>
            </div>
            {/* Accent glow */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl pointer-events-none" />
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <p className="text-primary-400 text-sm font-semibold uppercase tracking-widest mb-2">What Drives Us</p>
          <h3 className="text-2xl font-bold text-white mb-10">Our Core Values</h3>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Eye,
              title: "Transparency",
              description: "Clear communication, honest pricing, and no hidden surprises — ever."
            },
            {
              icon: TrendingUp,
              title: "Results-Driven",
              description: "Every solution is built to deliver measurable, tangible business value."
            },
            {
              icon: Compass,
              title: "Future-Ready",
              description: "We stay ahead of AI trends so your business stays ahead of the competition."
            }
          ].map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-gray-900/50 hover:bg-gray-900 border border-white/10 hover:border-white/20 rounded-xl p-7 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                <value.icon className="w-5 h-5 text-primary-300" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-3">{value.title}</h4>
              <p className="text-gray-400 leading-relaxed text-sm">{value.description}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default About;
